import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Copy, Download, FileText, Loader2, MessageCircle, RotateCcw, Send, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { ccaaLabel, modalityLabel } from './ccaa';
import { extrasAsRows, fullApplicantName } from './formFields';
import { LicenseApplicantSection } from './LicenseApplicantSection';
import { downloadLicenseDocumentPack, fileSlug } from './downloadLicenseDocuments';
import { downloadLicenseEvidence, openLicenseEvidence } from './openLicenseEvidence';
import { useLicenseOrderAction, useLicenseOrderDetail } from './hooks/useLicenseOrders';
import { licenseDeliveredAt, licenseHeaderWhen, licensePaidAt } from './licenseOrderDates';
import {
  deliverSavedBeforeRefusal,
  keepEditedLicenseFields,
  licenseMetadataBody,
  licenseValidityLabel,
  serverLicenseFields,
} from './licenseMetadataForm';
import { LicenseOperationsSection } from './LicenseOperationsSection';
import { LicenseInvoiceCorrectionPanel } from './LicenseInvoiceCorrectionPanel';
import { LicenseProductChangePanel } from './LicenseProductChangePanel';
import type { LicenseOrderAttribution, LicenseOrderDetail, LicenseOrderStatus } from './types';

interface Props {
  orderId: string | null;
  onClose: () => void;
  /** Abre el formulario de cambiar producto al cargar la ficha. */
  focusProductChange?: boolean;
}

const STATUS_LABEL: Record<LicenseOrderStatus, string> = {
  received: 'Sin pagar',
  paid: 'Pagado',
  validated: 'Validado',
  in_progress: 'En curso',
  awaiting_review: 'Pendiente',
  awaiting_captcha: 'Captcha',
  awaiting_3ds: 'Espera 3DS',
  submitted: 'Presentada',
  delivered: 'Entregado',
  failed: 'Fallido',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado',
  abandoned: 'Abandonado',
};

function fmtEur(cents: number): string {
  return (
    ((cents || 0) / 100).toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '\u00a0€'
  );
}

function unpaidWhatsappScript(detail: LicenseOrderDetail): string {
  const name = String(detail.applicant.firstName || '').trim() || 'ahí';
  const ccaa = ccaaLabel(detail.ccaa) || 'tu comunidad';
  const official = fmtEur(detail.officialFeeCents);
  const service = fmtEur(detail.serviceFeeCents);
  const total = fmtEur((detail.officialFeeCents || 0) + (detail.serviceFeeCents || 0));
  return (
    `Hola ${name}, Alberto de AiPeces. Vimos que empezaste a encargarnos la licencia de ${ccaa} y no llegaste a pagar.\n\n` +
    `Por si no quedó claro: la licencia son ${official} de tasa oficial. Eso lo cobra la administración, no nosotros. Si la sacas tú en la sede, pagas solo eso; en la web tienes la guía paso a paso.\n\n` +
    `Los ${service} son solo si prefieres delegarnos el trámite: te llega por email, resolvemos incidencias, te avisamos de la renovación, te enviamos una ficha de recomendaciones (tallas mínimas y normativa) y te regalamos 3 meses de modo Pro en la app. Con Pro tienes, además, un 10 % de cashback en material de pesca en la Tienda. El total en ese caso es ${total}.\n\n` +
    `Si ya la has sacado por tu cuenta, perfecto. Si más adelante quieres que te la renovemos, aquí estamos. Buena pesca.`
  );
}

function additionalDocsWhatsappScript(detail: LicenseOrderDetail): string {
  const name = String(detail.applicant.firstName || '').trim() || 'hola';
  const ccaa = ccaaLabel(detail.ccaa) || 'tu comunidad';
  return (
    `Hola ${name}, somos el equipo de AiPeces. Estamos revisando tu licencia de ${ccaa} ` +
    `y la administración nos pide documentación adicional para poder completar el trámite. ` +
    `Te indicamos por aquí exactamente qué necesitamos y cómo enviarlo de forma segura. ` +
    `Tu pedido ${detail.publicId} sigue en curso.`
  );
}

function hasAttribution(a: LicenseOrderAttribution): boolean {
  return Object.values(a).some((v) => Boolean(v));
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function Field({ label, value }: { label: string; value?: string | null }) {
  const text = value?.trim() ? value : '—';
  return (
    <div className="min-w-0">
      <div className="text-[11px] leading-tight text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground select-all break-words leading-snug">
        {text}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  className,
  action,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <section className={className}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function EvidenceFileActions({
  orderId,
  field,
  label,
  fileBase,
}: {
  orderId: string;
  field: string;
  label: string;
  fileBase: string;
}) {
  const [saving, setSaving] = useState(false);

  return (
    <div className="flex items-center gap-1 min-w-0">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 min-w-0 text-sm font-medium text-primary hover:underline"
        onClick={() => {
          void openLicenseEvidence(orderId, field, label).catch((err) => {
            window.alert(err instanceof Error ? err.message : 'No se pudo abrir el documento');
          });
        }}
      >
        <span className="truncate">{label}</span>
      </button>
      <button
        type="button"
        aria-label={`Descargar ${fileBase.replace(/-/g, ' ')}`}
        title={`Descargar ${fileBase.replace(/-/g, ' ')}`}
        disabled={saving}
        className="shrink-0 p-1 rounded text-muted-foreground hover:text-primary hover:bg-muted disabled:opacity-50"
        onClick={() => {
          void (async () => {
            setSaving(true);
            try {
              await downloadLicenseEvidence(orderId, field, fileBase);
            } catch (err) {
              window.alert(err instanceof Error ? err.message : 'No se pudo descargar el documento');
            } finally {
              setSaving(false);
            }
          })();
        }}
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
      </button>
    </div>
  );
}

function PowerOfAttorneyBlock({
  detail,
  pending,
  onGenerate,
}: {
  detail: LicenseOrderDetail;
  pending: boolean;
  onGenerate: () => Promise<void>;
}) {
  const power = detail.powerOfAttorney;
  const [saving, setSaving] = useState(false);
  if (!power?.applies) return null;
  const needsGeneration = power.status !== 'ready' && power.status !== 'whatsapp';

  return (
    <div className="mt-3 space-y-2">
      {power.downloadable && power.label && (
        <EvidenceFileActions
          orderId={detail.id}
          field="power_pdf"
          label={power.label}
          fileBase={fileSlug(power.label)}
        />
      )}
      {power.status === 'whatsapp' && (
        <p className="text-xs text-amber-600">
          La firma llegó por WhatsApp. Hace falta la firma dibujada para componer el poder.
        </p>
      )}
      {power.missing.length > 0 && power.status !== 'whatsapp' && (
        <p className="text-xs text-amber-600">
          Falta para el poder: {power.missing.map((item) => item.label).join(', ')}.
        </p>
      )}
      {power.status === 'stale' && (
        <p className="text-xs text-amber-600">Los datos han cambiado desde que se generó el poder.</p>
      )}
      {power.status !== 'whatsapp' && (
        <button
          type="button"
          disabled={pending || saving}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
          onClick={() => {
            void (async () => {
              setSaving(true);
              try {
                await onGenerate();
              } catch (err) {
                window.alert(err instanceof Error ? err.message : 'No se pudo generar el poder');
              } finally {
                setSaving(false);
              }
            })();
          }}
        >
          {(pending || saving) && <Loader2 size={12} className="animate-spin" />}
          {needsGeneration ? 'Generar poder' : 'Volver a generar'}
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: LicenseOrderStatus }) {
  const tone =
    status === 'awaiting_review' || status === 'awaiting_captcha' || status === 'awaiting_3ds'
      ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
      : status === 'submitted'
        ? 'bg-sky-500/10 text-sky-500 border-sky-500/30'
        : status === 'delivered'
        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
        : status === 'failed' || status === 'refunded'
          ? 'bg-rose-500/10 text-rose-500 border-rose-500/30'
          : status === 'abandoned'
            ? 'bg-slate-500/15 text-slate-300 border-slate-400/40'
            : 'bg-muted text-muted-foreground border-border';
  return (
    <span className={cn('inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium', tone)}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function LicenseOrderDetailDialog({
  orderId,
  onClose,
  focusProductChange = false,
}: Props) {
  const isOpen = Boolean(orderId);
  const { data: detail, isLoading, error, refetch } = useLicenseOrderDetail(orderId);
  const action = useLicenseOrderAction(orderId);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  const [packDownloading, setPackDownloading] = useState(false);
  const [officialNumber, setOfficialNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [holderDirty, setHolderDirty] = useState(false);
  const [metadataNotice, setMetadataNotice] = useState<string | null>(null);
  const [formOrderId, setFormOrderId] = useState<string | null>(null);
  const [copiedWhatsapp, setCopiedWhatsapp] = useState(false);
  const [deliverChoiceOpen, setDeliverChoiceOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [sendLastEmail, setSendLastEmail] = useState(true);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const applicantName = detail ? fullApplicantName(detail.applicant) : '';
  const detailId = detail?.id ?? null;

  if (detailId !== formOrderId) {
    const server = serverLicenseFields(detail);
    const provisional = /^pendiente(\s|$)/i.test(applicantName.trim());
    setFormOrderId(detailId);
    setMetadataDirty(false);
    setHolderDirty(false);
    setMetadataNotice(null);
    setOfficialNumber(server.officialNumber);
    setValidFrom(server.validFrom);
    setValidTo(server.validTo);
    setHolderName(provisional ? '' : applicantName);
    setAbandonOpen(false);
    setPaymentNotice(null);
  }

  useEffect(() => {
    if (!detail?.id || detail.id !== formOrderId) return;
    const server = serverLicenseFields(detail);
    const merged = keepEditedLicenseFields(
      { officialNumber, validFrom, validTo },
      server,
      metadataDirty
    );
    if (metadataDirty && !merged.dirty) {
      setMetadataDirty(false);
      if (officialNumber !== merged.fields.officialNumber) {
        setOfficialNumber(merged.fields.officialNumber);
      }
      if (validFrom !== merged.fields.validFrom) setValidFrom(merged.fields.validFrom);
      if (validTo !== merged.fields.validTo) setValidTo(merged.fields.validTo);
      return;
    }
    if (metadataDirty) return;
    if (officialNumber !== merged.fields.officialNumber) {
      setOfficialNumber(merged.fields.officialNumber);
    }
    if (validFrom !== merged.fields.validFrom) setValidFrom(merged.fields.validFrom);
    if (validTo !== merged.fields.validTo) setValidTo(merged.fields.validTo);
  }, [detail, formOrderId, metadataDirty, officialNumber, validFrom, validTo]);

  useEffect(() => {
    if (holderDirty) return;
    if ((detail?.id ?? null) !== formOrderId) return;
    const provisional = /^pendiente(\s|$)/i.test(applicantName.trim());
    const next = provisional ? '' : applicantName;
    setHolderName((current) => (current === next ? current : next));
  }, [holderDirty, detail?.id, formOrderId, applicantName]);

  useEffect(() => {
    setCopiedWhatsapp(false);
    setDeliverChoiceOpen(false);
  }, [detail?.id]);

  if (!isOpen) return null;

  function markLicenseEdit() {
    if (action.isError) action.reset();
    setMetadataNotice(null);
    setMetadataDirty(true);
  }

  function licenseFields() {
    return { officialNumber, holderName, validFrom, validTo };
  }

  async function onAttachPdf(file: File) {
    if (!orderId) return;
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    const pdfBase64 = btoa(binary);
    try {
      await action.mutateAsync({
        action: 'attach_pdf',
        pdfBase64,
        ...licenseMetadataBody(licenseFields()),
      });
    } catch {
      // El aviso queda en action.error. Los campos editados no se vacían.
    }
  }

  async function submitDeliver(sendEmail: boolean) {
    const metadata = licenseMetadataBody(licenseFields());
    try {
      await action.mutateAsync({
        action: 'deliver',
        sendEmail,
        ...metadata,
      });
      setDeliverChoiceOpen(false);
      setMetadataNotice(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (Object.keys(metadata).length > 0 && deliverSavedBeforeRefusal(message)) {
        setMetadataNotice(
          'Número, titular y vigencia quedan guardados. La entrega no se ha completado.'
        );
      }
    }
  }

  function onDeliverClick() {
    if (!detail) return;
    if (action.isError) action.reset();
    if (detail.hasPdf) {
      setDeliverChoiceOpen(true);
      return;
    }
    void submitDeliver(false);
  }

  async function onDownloadPack() {
    if (!detail) return;
    setPackDownloading(true);
    try {
      await downloadLicenseDocumentPack(detail);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo descargar la documentación');
    } finally {
      setPackDownloading(false);
    }
  }

  async function saveLicenseMetadata() {
    try {
      await action.mutateAsync({
        action: 'update_license_metadata',
        ...licenseMetadataBody(licenseFields()),
      });
      setMetadataNotice('Datos de licencia guardados');
    } catch {
      setMetadataNotice(null);
    }
  }

  const extras = extrasAsRows(detail?.recipeExtras, detail?.extraFields);
  const paidWhen = detail ? licensePaidAt(detail) : null;
  const deliveredWhen = detail ? licenseDeliveredAt(detail) : null;
  const headerWhen = detail ? licenseHeaderWhen(detail) : null;
  const validity = licenseValidityLabel(validFrom, validTo);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-5"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-7xl h-[min(92dvh,900px)] max-h-[calc(100dvh-2.5rem)] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-border shrink-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <h2 className="text-lg font-bold text-foreground truncate">
              Pedido {detail?.publicId ?? '…'}
            </h2>
            {detail && (
              <>
                <StatusBadge status={detail.status} />
                <span className="text-xs text-muted-foreground">
                  {headerWhen?.label ? `${headerWhen.label} ${fmtDate(headerWhen.at)}` : fmtDate(headerWhen?.at)}
                </span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors shrink-0"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && error && (
          <div className="m-5 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-500">
            No se pudo cargar el formulario: {error.message}
          </div>
        )}

        {!isLoading && detail && (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)] overflow-y-auto lg:overflow-hidden">
            <div className="lg:col-span-5 xl:col-span-5 min-h-0 p-5 space-y-4 lg:border-r border-border lg:overflow-y-auto scrollbar-thin">
              {detail.applicantDecryptFailed && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-600">
                  No se pudieron descifrar los datos personales. Teléfono y campos extra sí están
                  abajo si el usuario los envió.
                </div>
              )}

              <LicenseOperationsSection
                detail={detail}
                onUpdated={() => {
                  void refetch();
                }}
              />

              <LicenseApplicantSection
                detail={detail}
                pending={action.isPending}
                onSave={async (form) => {
                  await action.mutateAsync({ action: 'update_applicant', applicant: form });
                  const full = [form.firstName, form.lastName, form.secondLastName].filter(Boolean).join(' ');
                  if (full) {
                    setHolderName(full);
                    setHolderDirty(false);
                  }
                }}
              />

              <Section
                title="Documentación"
                action={
                  <button
                    type="button"
                    disabled={packDownloading}
                    title="Guarda todos los documentos y una ficha con los datos del solicitante"
                    onClick={() => {
                      void onDownloadPack();
                    }}
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    {packDownloading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Download size={12} />
                    )}
                    Descargar todo
                  </button>
                }
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {detail.documents?.identityFront && (
                    <EvidenceFileActions
                      orderId={detail.id}
                      field="identity_front"
                      label="Ver identidad · anverso"
                      fileBase="identidad-anverso"
                    />
                  )}
                  {detail.documents?.identityBack && (
                    <EvidenceFileActions
                      orderId={detail.id}
                      field="identity_back"
                      label="Ver identidad · reverso"
                      fileBase="identidad-reverso"
                    />
                  )}
                  {detail.documents?.additional.map((document) => (
                    <EvidenceFileActions
                      key={document.field}
                      orderId={detail.id}
                      field={document.field}
                      label={document.label}
                      fileBase={fileSlug(document.label)}
                    />
                  ))}
                </div>
                {detail.documents?.deliveryMode === 'whatsapp_pending' && (
                  <p className="text-xs text-amber-600 mt-2">
                    DNI pendiente: el cliente eligió enviarlo por WhatsApp. Solicítalo antes de tramitar.
                  </p>
                )}
                {detail.documents?.deliveryMode === 'postpay_pending' && (
                  <p className="text-xs text-amber-600 mt-2">
                    Documentación pendiente postpago (fotos/firma tras el cobro). No tramitar aún.
                  </p>
                )}
                <PowerOfAttorneyBlock
                  detail={detail}
                  pending={action.isPending}
                  onGenerate={async () => {
                    const data = await action.mutateAsync({ action: 'generate_power' });
                    const missing = data?.power?.missing ?? [];
                    if (missing.length > 0) {
                      window.alert(
                        `No se ha generado el poder. Falta: ${missing.map((item) => item.label).join(', ')}.`
                      );
                    }
                  }}
                />
              </Section>

              {extras.length > 0 && (
                <Section title="Campos extra del formulario">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {extras.map((row) =>
                      row.isEvidence ? (
                        <div key={row.id} className="min-w-0">
                          <div className="text-[11px] leading-tight text-muted-foreground">
                            {row.label}
                          </div>
                          <div className="mt-1">
                            <EvidenceFileActions
                              orderId={detail.id}
                              field={row.id}
                              label="Ver documento"
                              fileBase={fileSlug(row.label)}
                            />
                          </div>
                        </div>
                      ) : (
                        <Field key={row.id} label={row.label} value={row.value} />
                      )
                    )}
                  </div>
                </Section>
              )}
            </div>

            <div className="lg:col-span-4 xl:col-span-4 min-h-0 p-5 space-y-4 lg:border-r border-border lg:overflow-y-auto scrollbar-thin">
              <Section title="Licencia">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div className="col-span-2">
                    <Field label="Producto" value={detail.recipeName} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] leading-tight text-muted-foreground">Idioma</div>
                    <select
                      className="mt-0.5 w-full max-w-xs rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
                      value={detail.locale === 'en' ? 'en' : 'es'}
                      disabled={action.isPending}
                      onChange={(e) => {
                        const next = e.target.value === 'en' ? 'en' : 'es';
                        if (next === (detail.locale === 'en' ? 'en' : 'es')) return;
                        action.mutate({ action: 'set_locale', locale: next });
                      }}
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      Correos al cliente. El cambio queda en el historial del pedido.
                    </p>
                  </div>
                  <Field
                    label="Comunidad"
                    value={`${ccaaLabel(detail.ccaa)}${detail.tier ? ` · ${detail.tier}` : ''}`}
                  />
                  <Field label="Modalidad" value={modalityLabel(detail.modality)} />
                  <Field
                    label="Duración"
                    value={
                      (detail.durationLabel || detail.duration) +
                      (detail.exemption ? ` (${detail.exemption})` : '')
                    }
                  />
                  <Field label="Tasas (sin IVA)" value={fmtEur(detail.officialFeeCents)} />
                  <Field
                    label="Gestión (IVA 21 % incl.)"
                    value={fmtEur(detail.serviceFeeCents)}
                  />
                  <Field
                    label="Total"
                    value={fmtEur(detail.officialFeeCents + detail.serviceFeeCents)}
                  />
                  <Field
                    label="Factura"
                    value={detail.legalInvoiceNumber || (detail.paidAt ? 'Pendiente de emitir' : '—')}
                  />
                  <Field label="PDF oficial" value={detail.hasPdf ? 'Adjunto' : 'Pendiente'} />
                  <Field label="Nº oficial" value={officialNumber.trim() || detail.officialLicenseNumber} />
                  <Field label="Pagado" value={fmtDate(paidWhen)} />
                  <Field label="Entregado" value={fmtDate(deliveredWhen)} />
                  {detail.refundedAt && (
                    <Field label="Reembolsado" value={fmtDate(detail.refundedAt)} />
                  )}
                  {validity && <Field label="Vigencia" value={validity} />}
                </div>
                {detail.failureReason && detail.status !== 'refunded' && (
                  <div className="mt-3 text-sm text-rose-500">Error: {detail.failureReason}</div>
                )}
                {detail.sedeUrl && (
                  <a
                    href={detail.sedeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary text-sm mt-3 font-medium"
                  >
                    <FileText size={14} /> Abrir sede oficial
                  </a>
                )}
              </Section>

              <Section title="Origen">
                {detail.attribution && hasAttribution(detail.attribution) ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="utm_source" value={detail.attribution.utmSource} />
                    <Field label="utm_medium" value={detail.attribution.utmMedium} />
                    <Field label="utm_campaign" value={detail.attribution.utmCampaign} />
                    <Field label="utm_content" value={detail.attribution.utmContent} />
                    {detail.attribution.utmTerm && (
                      <Field label="utm_term" value={detail.attribution.utmTerm} />
                    )}
                    <div className="col-span-2">
                      <Field label="Página de entrada" value={detail.attribution.entryPath} />
                    </div>
                    <Field label="Procedencia" value={detail.attribution.referrerHost} />
                    <Field label="Primer toque" value={fmtDate(detail.attribution.firstTouchAt)} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin origen registrado.</p>
                )}
              </Section>
            </div>

            <div className="lg:col-span-3 xl:col-span-3 min-h-0 p-5 space-y-4 lg:overflow-y-auto scrollbar-thin">
              <Section title="Acciones">
                <div className="flex flex-col gap-2">
                  <label className="block">
                    <span className="text-[11px] text-muted-foreground">Nº de licencia (del PDF)</span>
                    <input
                      type="text"
                      value={officialNumber}
                      onChange={(e) => {
                        markLicenseEdit();
                        setOfficialNumber(e.target.value);
                      }}
                      placeholder="p. ej. 2301960"
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-muted-foreground">
                      Nombre titular (para el correo)
                    </span>
                    <input
                      type="text"
                      value={holderName}
                      onChange={(e) => {
                        markLicenseEdit();
                        setHolderDirty(true);
                        setHolderName(e.target.value);
                      }}
                      placeholder="Nombre real del PDF (no «Pendiente»)"
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-[11px] text-muted-foreground">Vigencia desde</span>
                      <input
                        type="date"
                        value={validFrom}
                        onChange={(e) => {
                          markLicenseEdit();
                          setValidFrom(e.target.value);
                        }}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-muted-foreground">Vigencia hasta</span>
                      <input
                        type="date"
                        value={validTo}
                        onChange={(e) => {
                          markLicenseEdit();
                          setValidTo(e.target.value);
                        }}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <label className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm cursor-pointer hover:bg-muted/80">
                    <Upload size={14} />
                    {detail.hasPdf ? 'Sustituir PDF' : 'Adjuntar PDF'}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.currentTarget.value = '';
                        if (f) void onAttachPdf(f);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={
                      action.isPending ||
                      (!officialNumber.trim() && !validFrom && !validTo)
                    }
                    onClick={() => {
                      void saveLicenseMetadata();
                    }}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/10 text-primary text-sm disabled:opacity-50"
                  >
                    {action.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                    Guardar datos de licencia
                  </button>
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    Copia nº, nombre y vigencia del PDF de la sede. Guardar datos no entrega ni
                    envía correo. Si Entregar falla, el número y la vigencia ya quedan guardados.
                    Adjuntar el PDF tampoco envía correo.
                  </p>
                  {(detail.status === 'received' || detail.status === 'abandoned') && !abandonOpen && (
                    <button
                      type="button"
                      disabled={action.isPending}
                      onClick={() => {
                        void (async () => {
                          try {
                            await action.mutateAsync({ action: 'send_payment_link' });
                            setPaymentNotice(
                              'Correo enviado con el enlace de pago. Caduca en 24 horas.'
                            );
                          } catch (err) {
                            window.alert(
                              err instanceof Error ? err.message : 'No se pudo enviar el enlace'
                            );
                          }
                        })();
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm disabled:opacity-50"
                    >
                      {action.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Enviar enlace de pago
                    </button>
                  )}
                  {detail.status === 'received' && !abandonOpen && (
                    <button
                      type="button"
                      disabled={action.isPending}
                      onClick={() => {
                        setSendLastEmail(true);
                        setAbandonOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-muted text-foreground text-sm disabled:opacity-50"
                    >
                      Marcar abandonado
                    </button>
                  )}
                  {detail.status === 'received' && abandonOpen && (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <p className="text-sm text-foreground">
                        Sale de Sin pagar y de la cola. Si más adelante pagan el enlace, el pedido
                        vuelve solo a pendientes.
                      </p>
                      <label className="flex items-start gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={sendLastEmail}
                          onChange={(e) => setSendLastEmail(e.target.checked)}
                        />
                        <span>Enviar ahora el último correo con el enlace de pago (caduca en 24 h).</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={action.isPending}
                          onClick={() => {
                            void (async () => {
                              try {
                                await action.mutateAsync({
                                  action: 'abandon',
                                  sendEmail: sendLastEmail,
                                });
                                setAbandonOpen(false);
                                setPaymentNotice(
                                  sendLastEmail
                                    ? 'Marcado como abandonado y correo enviado.'
                                    : 'Marcado como abandonado.'
                                );
                              } catch (err) {
                                window.alert(
                                  err instanceof Error ? err.message : 'No se pudo abandonar'
                                );
                              }
                            })();
                          }}
                          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background text-sm disabled:opacity-50"
                        >
                          {action.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                          Confirmar abandonado
                        </button>
                        <button
                          type="button"
                          disabled={action.isPending}
                          onClick={() => setAbandonOpen(false)}
                          className="px-3 py-2 text-sm text-muted-foreground"
                        >
                          Seguir en Sin pagar
                        </button>
                      </div>
                    </div>
                  )}
                  {detail.status === 'abandoned' && (
                    <button
                      type="button"
                      disabled={action.isPending}
                      onClick={() => {
                        if (!window.confirm('¿Devolver este pedido a Sin pagar?')) return;
                        void (async () => {
                          try {
                            await action.mutateAsync({ action: 'restore_unpaid' });
                            setPaymentNotice('El pedido vuelve a Sin pagar.');
                          } catch (err) {
                            window.alert(
                              err instanceof Error ? err.message : 'No se pudo devolver a Sin pagar'
                            );
                          }
                        })();
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-muted text-foreground text-sm disabled:opacity-50"
                    >
                      Volver a Sin pagar
                    </button>
                  )}
                  {(detail.status === 'received' || detail.status === 'cancelled') && (
                    <button
                      type="button"
                      onClick={() => {
                        const text = unpaidWhatsappScript(detail);
                        void navigator.clipboard.writeText(text).then(() => {
                          setCopiedWhatsapp(true);
                          window.setTimeout(() => setCopiedWhatsapp(false), 2500);
                        });
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-600 text-sm"
                    >
                      {copiedWhatsapp ? <Copy size={14} /> : <MessageCircle size={14} />}
                      {copiedWhatsapp ? 'WhatsApp copiado' : 'Copiar WhatsApp (sin pagar)'}
                    </button>
                  )}
                  {detail.paidAt && detail.status !== 'delivered' && detail.status !== 'refunded' && (
                    <button
                      type="button"
                      onClick={() => {
                        const text = additionalDocsWhatsappScript(detail);
                        void navigator.clipboard.writeText(text).then(() => {
                          setCopiedWhatsapp(true);
                          window.setTimeout(() => setCopiedWhatsapp(false), 2500);
                        });
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-600 text-sm"
                    >
                      <MessageCircle size={14} />
                      {copiedWhatsapp ? 'WhatsApp copiado' : 'Pedir documentación por WhatsApp'}
                    </button>
                  )}
                  {detail.paidAt && detail.status !== 'refunded' && detail.status !== 'cancelled' && (
                    <LicenseProductChangePanel
                      orderId={detail.id}
                      detail={detail}
                      startOpen={focusProductChange}
                    />
                  )}
                  <LicenseInvoiceCorrectionPanel orderId={detail.id} detail={detail} />
                  {detail.paidAt && (
                    <button
                      type="button"
                      disabled={invoiceDownloading}
                      onClick={() => {
                        void (async () => {
                          if (!orderId) return;
                          setInvoiceDownloading(true);
                          try {
                            await downloadLegalInvoicePdf(
                              detail.legalInvoiceId || orderId,
                              detail.legalInvoiceNumber ?? 'factura'
                            );
                          } catch (err) {
                            window.alert(
                              err instanceof Error ? err.message : 'Error al descargar la factura'
                            );
                          } finally {
                            setInvoiceDownloading(false);
                          }
                        })();
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm disabled:opacity-50"
                    >
                      {invoiceDownloading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      {detail.legalInvoiceNumber || 'Descargar factura'}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={
                      action.isPending ||
                      !detail.paidAt ||
                      detail.status === 'delivered' ||
                      detail.status === 'refunded' ||
                      detail.status === 'cancelled'
                    }
                    onClick={onDeliverClick}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50"
                  >
                    <Send size={14} />
                    Entregar
                  </button>
                  <button
                    type="button"
                    disabled={action.isPending || detail.status === 'refunded'}
                    onClick={() => {
                      if (window.confirm('¿Reembolsar este pedido en Stripe?')) {
                        action.mutate({ action: 'refund', note: 'Reembolso desde el portal' });
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 text-rose-500 text-sm disabled:opacity-50"
                  >
                    <RotateCcw size={14} />
                    Reembolsar
                  </button>
                </div>
                {!deliverChoiceOpen && action.error && (
                  <div className="mt-2 text-sm text-rose-500">{action.error.message}</div>
                )}
                {!deliverChoiceOpen && metadataNotice && (
                  <p className="mt-2 text-sm text-emerald-600" role="status">
                    {metadataNotice}
                  </p>
                )}
                {!deliverChoiceOpen && paymentNotice && (
                  <p className="mt-2 text-sm text-emerald-600" role="status">
                    {paymentNotice}
                  </p>
                )}
              </Section>

              {detail.events?.length > 0 && (
                <Section title="Historial">
                  <div className="space-y-2">
                    {detail.events.map((ev) => (
                      <div key={ev.id} className="text-sm">
                        <div className="text-[11px] text-muted-foreground tabular-nums">
                          {fmtDate(ev.at)}
                        </div>
                        <div className="min-w-0 leading-snug">
                          <span className="font-medium">{ev.event}</span>
                          <span className="text-muted-foreground"> · {ev.actor}</span>
                        </div>
                        {typeof ev.detail?.note === 'string' && ev.detail.note ? (
                          <div className="text-muted-foreground">{ev.detail.note}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>
        )}
      </div>

      {deliverChoiceOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => {
            if (!action.isPending) setDeliverChoiceOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliver-choice-title"
            className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="deliver-choice-title" className="text-base font-semibold text-foreground">
              ¿Cómo entregar este pedido?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-snug">
              Hay un PDF adjunto. Elige si el sistema debe enviar el correo al cliente (con copia a
              pedidos) o solo marcar como entregado sin correo. Si el servidor rechaza la entrega,
              este diálogo sigue abierto.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                disabled={action.isPending}
                onClick={() => {
                  void submitDeliver(true);
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
              >
                {action.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {action.isPending ? 'Entregando…' : 'Entregar y enviar correo al cliente'}
              </button>
              <button
                type="button"
                disabled={action.isPending}
                onClick={() => {
                  void submitDeliver(false);
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-muted text-foreground text-sm font-medium disabled:opacity-50"
              >
                Entregar sin enviar correo
              </button>
              <button
                type="button"
                disabled={action.isPending}
                onClick={() => setDeliverChoiceOpen(false)}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
            {action.error && (
              <div className="mt-3 text-sm text-rose-500">{action.error.message}</div>
            )}
            {metadataNotice && (
              <p className="mt-2 text-sm text-emerald-600" role="status">
                {metadataNotice}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
